import { useMemo } from 'react';
import { FillTag, OutlineTag } from '@doda/io-react/common';
import type { JobListKodawariResponse } from '@doda/api-client/aspida/@types';
import {
  KODAWARI,
  JOB_CARD_KODAWARI,
  type KodawariType,
} from '@doda/common/src/constants/kodawari';
import styles from './index.module.scss';

type EmphasizedTag = 'isNewArrival' | 'isNearDeadline';
export type EmphasizedTagListProps = Pick<
  JobListKodawariResponse,
  EmphasizedTag
> & {
  isCareerAdvisorJob?: boolean;
};

export const EmphasizedTagList = ({
  isNewArrival,
  isNearDeadline,
  isCareerAdvisorJob = false,
}: EmphasizedTagListProps) => {
  return (
    <>
      {isNewArrival && (
        <FillTag color="red" className={styles['jobCard-tag']}>
          New
        </FillTag>
      )}
      {isNearDeadline && (
        <OutlineTag color="red" className={styles['jobCard-tag']}>
          締切間近
        </OutlineTag>
      )}
      {isCareerAdvisorJob && (
        <OutlineTag color="orange" className={styles['jobCard-tag']}>
          キャリアアドバイザー紹介求人
        </OutlineTag>
      )}
    </>
  );
};

export type KodawariTagListProps = Omit<JobListKodawariResponse, EmphasizedTag>;

/**
 * レスポンスに含まれるIDをタグ表示用テキストへ変換したデータを生成する
 */
function createKodawariTag(kodawari: KodawariTagListProps): {
  employmentStatus: string[];
  otherTags: string[];
} {
  const kodawariTag: Record<KodawariType, { value: string; order: number }[]> =
    {
      employmentStatus: [],
      requirements: [],
      workplaceEnvironment: [],
    };
  const keys = Object.keys(kodawari);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i] as KodawariType;
    const kodawariResponse: string[] | undefined = kodawari[key];
    // eslint-disable-next-line no-continue
    if (!kodawariResponse || kodawariResponse.length === 0) continue;
    // APIではDBにある値が全て返却されるため、表示する値のみ取り出す
    const filteredResponse = JOB_CARD_KODAWARI[key].filter(({ value }) =>
      kodawariResponse.includes(value)
    );
    // ID→テキスト変換
    for (let j = 0; j < filteredResponse.length; j += 1) {
      const { value, order } = filteredResponse[j];
      const text = KODAWARI[key].find((idMap) => value === idMap.value)?.text;
      if (text) kodawariTag[key].push({ value: text, order });
    }
  }
  // 順序をソートして返却する
  return {
    employmentStatus: kodawariTag.employmentStatus
      .sort((a, b) => a.order - b.order)
      .map(({ value }) => value),
    otherTags: [
      ...kodawariTag.requirements,
      ...kodawariTag.workplaceEnvironment,
    ]
      .sort((a, b) => a.order - b.order)
      .map(({ value }) => value),
  };
}

export const KodawariTagList = ({
  employmentStatus,
  requirements,
  workplaceEnvironment,
}: KodawariTagListProps) => {
  const kodawariTag = useMemo(
    () =>
      createKodawariTag({
        employmentStatus,
        requirements,
        workplaceEnvironment,
      }),
    [employmentStatus, requirements, workplaceEnvironment]
  );
  return (
    <>
      {kodawariTag.employmentStatus.map((text) => (
        <FillTag key={text} color="blue" className={styles['jobCard-tag']}>
          {text}
        </FillTag>
      ))}
      {kodawariTag.otherTags.map((text) => (
        <OutlineTag key={text} color="blue" className={styles['jobCard-tag']}>
          {text}
        </OutlineTag>
      ))}
    </>
  );
};
